import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../utils/axios";
import { NotificationManager } from "react-notifications";

import MainA from "../../layout/Main Admin";
import Loader from "../../components/atoms/Loader";
import Button from "../../components/atoms/Button";
import Anchor from "../../components/atoms/Anchor";
import Modal from "../../components/atoms/Modal";

import edit from "../../assets/icons/edit.svg";
import copy from "../../assets/icons/copy.svg";
import plus from "../../assets/icons/plus.svg";
import fields from "../../assets/icons/fields.svg";
import preview from "../../assets/icons/preview.svg";
import Table from "../../components/atoms/Table";
import ToggleSwitch from "../../components/atoms/ToggleSwitch";
import Input from "../../components/atoms/Input";
import CustomEditor from "../../components/atoms/CustomEditor";

import moment from "moment";

import "./styles.scss";

const baseUrl =  "https://dynamicproductstorage.blob.core.windows.net/form/";

const Forms = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateModalData, setDuplicateModalData] = useState({});
  const [eventData, setEventData] = useState([]);
  const user = JSON.parse(localStorage.getItem("currentUser")) || {};
  const navigate = useNavigate();

  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [modalEventNames, setModalEventNames] = useState([]);

  const [form, setForm] = useState({
    formId: 0,
    name: "",
    title: "",
    description: "",
    isActive: true,
    createdBy: user.userFirstName || "Admin",
  });
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchForms();
    getEvents();
  }, []);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const [formResp, groupResp] = await Promise.all([getRequest("Form/GetForms"), getRequest(`FormGroup/GetFormGroups`)]);

      if (formResp?.status === 200 && groupResp?.status === 200) {
        formResp.data.forEach((form) => {
          const groups = groupResp.data.filter((g) => g.formId === form.formId);
          const uniqueEventIds = [...new Set(groups.map((g) => g.eventId))];

          form.uniqueEvents = uniqueEventIds.length > 0 ? uniqueEventIds : "";
        })
        setForms(Array.isArray(formResp.data) ? formResp.data : []);
      } else {
        setForms([]);
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to fetch forms");
    } finally {
      setLoading(false);
    }
  };

  const getEvents = async () => {
      try {
        const resp = await getRequest(
          `Page/GetPagesByCategoryType/26`
        );
        setEventData(resp.data);
      } catch (error) {
        console.error("Error fetching event:", error);
      }
  };

  const openCreate = () => {
    setEditing(false);
    setForm({
      formId: 0,
      name: "",
      title: "",
      description: "",
      isActive: true,
      createdBy: user.userFirstName || "Admin",
    });
    setShowForm(true);
  };

  const openEdit = async (form) => {
    setForm(form);
    setEditing(true);
    setShowForm(true);
  };

  const saveForm = async () => {
    if (!form.name || form.name.trim() === "") {
      NotificationManager.error("Please enter form name");
      return;
    }

    try {
      let resp;

      if (form.formId > 0) {
        resp = await postRequest("Form/UpdateForm", form);
      } else {
        resp = await postRequest("Form/CreateForm", form);
      }

      if (resp?.status === 200 || resp?.status === 201) {
        NotificationManager.success(
          editing ? "Form updated successfully" : "Form created successfully",
        );

        setShowForm(false);
        fetchForms();
      } else {
        NotificationManager.error("Failed to save form");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Error saving form");
    }
  };

  const toggleStatus = async (formItem) => {
    if (!formItem) return;

    const id = formItem.formId ?? formItem.FormId;
    const newStatus = !(formItem.isActive ?? formItem.IsActive);

    try {
      const resp = await getRequest(`Form/ChangeStatus/${id}/${newStatus}`);

      if (resp?.status === 200) {
        NotificationManager.success("Status updated");

        setForms((prev) =>
          prev.map((f) =>
            (f.formId ?? f.FormId) === id ? { ...f, isActive: newStatus } : f,
          ),
        );
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to update status");
    }
  };

  const truncate = (text, length = 50) => {
    if (!text) return "";
    return text.length > length ? text.substring(0, length) + "..." : text;
  };

  const handleCloseDuplicateModal = () => {
    setDuplicateModalData({});
    setDuplicateModalOpen(false);
  };

  const handleDuplicateEntity = (data) => {
    setDuplicateModalOpen(true);
    setDuplicateModalData(data);
  };

  const handleDuplicate = () => {
    setLoading(true);
    setDuplicateModalOpen(false);
    try {
      getRequest(`Form/DuplicateForm/${duplicateModalData.formId}`).then((resp) => {
        if (resp?.status === 200 || resp?.status === 201) {
          NotificationManager.success("Form duplicated successfully");
          fetchForms();
        } else {
          NotificationManager.error("Failed to duplicate form");
        }
      });
    } catch (err) {
      console.error(err);
      NotificationManager.error("Error duplicating form");
    } finally {
      setLoading(false);
    }
  };

  const handleShowEvents = (uniqueIds) => {
    const ids = Array.isArray(uniqueIds) ? uniqueIds : [];

    const events = ids.map((id) => {
      const ev = eventData.find(
        (e) => e.pageId === id || e.pageId === Number(id)
      );

      return {
        pageId: id,
        pageName: ev?.pageName || ev?.pageTitle || `Event ${id}`,
      };
    });

    setModalEventNames(events);
    setEventModalOpen(true);
  };

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFields, setPreviewFields] = useState([]);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const openPreview = async (formId) => {
    setPreviewLoading(true);
    setPreviewFields([]);
    try {
      const resp = await getRequest(`FormField/GetByForm/${formId}`);
      let fieldsData = Array.isArray(resp?.data) ? resp.data : [];

      try {
        const optResp = await getRequest(`FieldOption/GetFieldOptionByFormId/${formId}`);
        if (optResp?.status === 200 && Array.isArray(optResp.data)) {
          const map = {};
          optResp.data.forEach((o) => {
            const fid = o.FormFieldId ?? o.formFieldId ?? 0;
            if (!map[fid]) map[fid] = [];
            map[fid].push({
              FieldOptionId: o.fieldOptionId ?? o.FieldOptionId ?? 0,
              FormFieldId: fid,
              OptionLabel: o.optionLabel ?? o.OptionLabel ?? o.Option ?? o.Value ?? String(o.optionLabel ?? o.Option ?? ""),
              OptionValue: o.optionValue ?? o.OptionValue ?? o.Value ?? o.option ?? String(o.optionValue ?? ""),
              OrderIndex: o.orderIndex ?? o.OrderIndex ?? 0,
            });
          });

          fieldsData = fieldsData.map((f) => {
            const fid = f.formFieldId ?? f.FormFieldId ?? 0;
            const opts = (map[fid] || []).slice().sort((a,b) => (a.OrderIndex??0)-(b.OrderIndex??0));
            const optionsArray = opts.map((x) => ({...x}));
            const optionsCsv = optionsArray.map((o) => o.OptionValue ?? o.OptionLabel).filter(Boolean).join(",");
            return {...f, Options: optionsCsv, OptionsArray: optionsArray};
          });
        } else {
          fieldsData = fieldsData.map((f) => {
            const raw = (f.Options ?? f.options ?? "").toString().split(",").map(s => s.trim()).filter(Boolean);
            const opts = raw.map((v,i) => ({FieldOptionId:0, FormFieldId: f.formFieldId ?? f.FormFieldId ?? 0, OptionLabel:v, OptionValue:v, OrderIndex:i}));
            return {...f, Options: raw.join(","), OptionsArray: opts};
          });
        }
      } catch (err) {
        fieldsData = fieldsData.map((f) => {
          const raw = (f.Options ?? f.options ?? "").toString().split(",").map(s => s.trim()).filter(Boolean);
          const opts = raw.map((v,i) => ({FieldOptionId:0, FormFieldId: f.formFieldId ?? f.FormFieldId ?? 0, OptionLabel:v, OptionValue:v, OrderIndex:i}));
          return {...f, Options: raw.join(","), OptionsArray: opts};
        });
      }

      fieldsData = (fieldsData || []).slice().sort((a,b) => (a.orderIndex ?? a.OrderIndex ?? 0) - (b.orderIndex ?? b.OrderIndex ?? 0)).map((f, idx) => ({...f, OrderIndex: f.orderIndex ?? f.OrderIndex ?? idx}));
      setPreviewFields(fieldsData);
      setPreviewModalOpen(true);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderPreviewField = (f, idx) => {
    const type = (f.FieldType ?? f.fieldType ?? "").toString().toLowerCase();
    const val = f.DefaultValue ?? f.defaultValue ?? "";
    const options = f.OptionsArray || [];
    const label = <label className="preview-label">{f.Label || f.label}</label>;
    const placeholder = f.Placeholder ?? f.placeholder ?? "";
    const required = !!(f.IsRequired ?? f.isRequired);

    switch (type) {
      case "textarea":
        return (
          <div className="preview-row" key={idx}>
            <span>{label}{required && <span className="important">*</span>}</span>
            <textarea defaultValue={val} placeholder={placeholder} disabled required={required} />
          </div>
        );
      case "select":
        return (
          <div className="preview-row" key={idx}>
            <span>{label}{required && <span className="important">*</span>}</span>
            <select disabled required={required}>
              <option value="">-- select --</option>
              {options.map((o,i) => (
                <option key={i} value={o.OptionValue ?? o.OptionLabel}>{o.OptionLabel ?? o.OptionValue}</option>
              ))}
            </select>
          </div>
        );
      case "checkbox":
        if (options.length > 0) {
          return (
            <div className="preview-row" key={idx}>
              <span>{label}{required && <span className="important">*</span>}</span>
              <div className="preview-multi">
                {options.map((o,i) => (
                  <label key={i} className="preview-inline"><input type="checkbox" disabled required={required} /> {o.OptionLabel ?? o.OptionValue}</label>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div className="preview-row" key={idx}>
            <label className="preview-inline"><input type="checkbox" disabled required={required} /> {f.Label}</label>
          </div>
        );
      case "radio":
        return (
          <div className="preview-row" key={idx}>
            <span>{label}{required && <span className="important">*</span>}</span>
            <div className="preview-multi">
              {options.map((o,i) => (
                <label key={i} className="preview-inline"><input name={`preview-${idx}`} type="radio" disabled required={required} /> {o.OptionLabel ?? o.OptionValue}</label>
              ))}
            </div>
          </div>
        );
      case "image":
        return (
          <div className="preview-row" key={idx}>
            <span>{label}{required && <span className="important">*</span>}</span>
            <div className="preview-images">
              {options.map((o,i) => (
                <img key={i} src={baseUrl + o.OptionValue} alt={o.OptionLabel} style={{ maxWidth: 120, maxHeight: 80, marginRight: 8 }} />
              ))}
            </div>
          </div>
        );
      default:
        const inputType = ["number","email","date","time"].includes(type) ? type : "text";
        return (
          <div className="preview-row" key={idx}>
            <span>{label}{required && <span className="important">*</span>}</span>
            <input type={inputType} defaultValue={val} placeholder={placeholder} disabled required={required} />
          </div>
        );
    }
  };

  const handleCloseEventModal = () => {
    setModalEventNames([]);
    setEventModalOpen(false);
  };

  const displayedForms = (Array.isArray(forms) ? forms.slice() : [])
    .filter((f) => {
      if (!showActiveOnly) return true;
      return !!(f.isActive ?? f.IsActive);
    })
    .filter((f) => {
      const q = String(searchQuery || "").trim().toLowerCase();
      if (!q) return true;
      const name = String(f.name ?? f.Name ?? "").toLowerCase();
      const title = String(f.title ?? f.Title ?? "").toLowerCase();
      const desc = String(f.description ?? f.Description ?? "").toLowerCase();
      return name.includes(q) || title.includes(q) || desc.includes(q);
    })
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

  return (
    <MainA className="b-forms-wrapper">
      <Loader loading={loading} />

      <Modal
        isOpen={duplicateModalOpen}
        closeModal={handleCloseDuplicateModal}
        className="modal-2"
      >
        <div className="circle">!</div>
        <div>
          <h4>Are you sure? </h4>
          <p>You want to make dupicate of entity</p>
          <div className="v-modal-button-wrapper">
            <Button onClick={handleDuplicate}>Yes, Duplicate entity</Button>
            <Button onClick={handleCloseDuplicateModal} type="primary">
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={eventModalOpen}
        closeModal={handleCloseEventModal}
        className="modal-2 events-modal"
      >
        <h4>Events</h4>
        <div className="event-list">
          {modalEventNames && modalEventNames.length > 0 ? (
            <ul>
              {modalEventNames.map((ev, idx) => (
                <li key={`${ev.pageId}-${idx}`}><Anchor to={`/form-group/${ev.pageId}`}>{ev.pageName}</Anchor></li>
              ))}
            </ul>
          ) : (
            <p>No events found</p>
          )}
        </div>
        <div className="v-modal-button-wrapper">
          <Button onClick={handleCloseEventModal}>Close</Button>
        </div>
      </Modal>

      <Modal
        isOpen={previewModalOpen}
        closeModal={() => { setPreviewModalOpen(false); setPreviewFields([]); }}
        className="modal-2 pre-modal"
      >
        <h4>Form Preview</h4>
        <div className="preview-body">
          {previewLoading ? (
            <p>Loading...</p>
          ) : previewFields && previewFields.length > 0 ? (
            <div className="form-card preview-form-card">
              <form className="dynamic-form" onSubmit={(e) => e.preventDefault()}>
                <p className="form-sub">Please fill out the fields below - <span className="important">* Indicates required question</span></p>
                {previewFields.map((f, idx) => renderPreviewField(f, idx))}
              </form>
            </div>
          ) : (
            <p>No fields to preview</p>
          )}
        </div>
        <div className="v-modal-button-wrapper">
          <Button onClick={() => { setPreviewModalOpen(false); setPreviewFields([]); }}>Close</Button>
        </div>
      </Modal>

      <div className="wrapper">
        <div className="head">
          <h4>{!showForm ? "Forms" : editing ? "Edit Form" : "Create Form"}</h4>

          <div className="view-toggle">
            
            {showForm ? (
              <Button onClick={() => setShowForm(false)}>Cancel</Button>
            ) : (
              <>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ margin: 0, fontSize: 14 }}>All</label>
                <ToggleSwitch checked={showActiveOnly} onChange={(e) => setShowActiveOnly(e.target.checked)} />
                <label style={{ margin: 0, fontSize: 14 }}>Active</label>
              </div>
              <Button onClick={openCreate}>
                <img src={plus} alt="add" /> Create Form
              </Button>
              </>
            )}
          </div>
        </div>

        <div className="form-table">
          {showForm ? (
            <div className="data">
              <div className="row">
                <div className="add">
                  <label>Name</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div className="add">
                  <label>Title</label>
                  <Input
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="row">
                <div className="add description">
                  <label>Description</label>
                  <CustomEditor
                    data={form.description}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setForm({ ...form, description: data });
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="add">
                  <label>Active</label>

                  <ToggleSwitch
                    checked={!!form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                  />
                </div>
              </div>

              <div className="actions-row">
                <Button onClick={saveForm}>
                  {editing ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          ) : (
            <Table pagination={true} pageSize={10} totalRows={displayedForms.length}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Title</th>
                  <th style={{textAlign:"center"}}>Events</th>
                  <th>Created Date</th>
                  <th style={{textAlign:"center"}}>Responses</th>
                  <th>Active</th>
                  <th style={{textAlign:"right"}}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {displayedForms && displayedForms.length > 0 ? (
                  displayedForms.map((f) => {
                    const id = f.formId ?? f.FormId;

                    return (
                      <tr key={id}>
                        <td>{f.name}</td>
                        <td>{f.title}</td>
                        <td style={{textAlign:"center"}}>
                          {Array.isArray(f.uniqueEvents) && f.uniqueEvents.length > 0 ? (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleShowEvents(f.uniqueEvents);
                              }}
                            >
                              {f.uniqueEvents.length}
                            </a>
                          ) : (
                            0
                          )}
                        </td>
                        <td>{moment(f.createdDate).format("MMMM DD, YYYY")}</td>
                        <td style={{textAlign:"center"}}>{f.formResponseCount === 0 ? 0 : <a href={`/form-responses/${f.uniqueEvents[0]}`}>{f.formResponseCount}</a>}</td>
                        <td>
                          <ToggleSwitch
                            checked={!!f.isActive}
                            onChange={() => toggleStatus(f)}
                          />
                        </td>

                        <td style={{textAlign:"right"}}>
                            <Anchor onClick={() => navigate(`/forms/fields/${id}`)} type="primary" title="Edit Fields">
                              <img src={fields} alt="Edit Fields" />
                            </Anchor>
                          <Anchor onClick={() => openPreview(id)} type="primary" title="Preview Form">
                            <img src={preview} alt="Preview" />
                          </Anchor>
                          <Anchor
                            onClick={() => openEdit(f)}
                            type="primary"
                            title="Edit Form"
                            data-testid="edit-button"
                          >
                            <img src={edit} alt="Edit Form" />
                          </Anchor>
                          <Anchor
                            onClick={() => handleDuplicateEntity(f)}
                            type="primary"
                            title="Duplicate Form"
                            data-testid="duplicate-button"
                          >
                            <img src={copy} alt="Duplicate Form" />
                          </Anchor>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      No forms found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </MainA>
  );
};

export default Forms;
